import { Alert, AlertTitle, Box, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, TextField, MenuItem, Stack, Button, CircularProgress } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { enqueueSnackbar } from "notistack";
import { EldVehicle } from "src/models";
import Iconify from "src/components/iconify";
import { DownloadExcelButton, ReportFilter } from "../runner/report-runner";

type IftaTripLine = {
  usState: string,
  miles: number,
}

type IftaTripReportLinesProps = {
  lines: IftaTripLine[],
}

const IftaTripReportLines = memo(({ lines }: IftaTripReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="usState">
                State
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="miles" align="right">
                Miles
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.usState}
                </TruxTableCell>
                <TruxTableCell align="right">
                  {line.miles?.toFixed(2)}
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div>;
});

type PageModel = {
  lines?: IftaTripLine[],
  eldVehicles?: EldVehicle[],
}

export default function Page() {
  const settings = useSettingsContext();

  const currentRequest = useRef({} as any);

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [usState, setUsState] = useState<string>("");
  const [states, setStates] = useState<string[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);

  useEffect(() => {
    if (vehicleId && fromDate && toDate) {
      setStatesLoading(true);
      setUsState(""); 
      axios.get(`${endpoints.reports.iftaTrip.states}?vehicleId=${vehicleId}&fromDate=${fromDate}&toDate=${toDate}`)
        .then(response => {
          setStates(response.data || []);
          setStatesLoading(false);
        })
        .catch(() => {
          setStates([]);
          setStatesLoading(false);
        });
    } else {
      setStates([]);
      setUsState("");
    }
  }, [vehicleId, fromDate, toDate]);

  const dataLoader = useCallback(() => ({
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.iftaTrip.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const vehicles = useMemo(() => 
    data?.eldVehicles?.filter(v => v.active).map(v => ({ id: v.id, vehicleUnitNumber: v.vehicleUnitNumber })) || [],
    [data?.eldVehicles]
  );

  const filters: ReportFilter[] = [
    {
      initialState: {},
      render: () => null,
      addToRequest: () => {},
      validate: (request) => {
        if (!request.fromDate || !request.toDate) {
          return "Please select a date range";
        }
        return null;
      },
    },
    {
      initialState: {},
      render: () => null,
      addToRequest: () => {},
      validate: (request) => {
        if (!request.vehicleId) {
          return "Please select a vehicle";
        }
        return null;
      },
    },
  ];

  const handleRun = () => {
    if (!fromDate || !toDate) {
      enqueueSnackbar("Please select a date range", { variant: "error" });
      return;
    }
    if (!vehicleId) {
      enqueueSnackbar("Please select a vehicle", { variant: "error" });
      return;
    }

    const request: Record<string, any> = {
      ran: true,
      fromDate,
      toDate,
      vehicleId,
    };
    
    if (usState) {
      request.usState = usState;
    }

    currentRequest.current = request;
    setShouldRefresh(true);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="IFTA Trip Report">
          <DownloadExcelButton baseUrl={endpoints.reports.iftaTrip.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <Alert 
          icon={<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}><Iconify icon="mdi:robot-outline" /></Box>} 
          severity="success" 
          sx={{ mb: 2 }}
        >
          <AlertTitle>AI-Powered Mileage Recovery</AlertTitle>
          When the ELD is temporarily disconnected, our system detects missing miles using odometer readings and intelligently distributes them across states based on your recorded travel patterns. This ensures your IFTA report captures all miles driven.
        </Alert>

        <Card sx={{ mb: 2 }}>
          <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2 }}>
            <DatePicker
              label="From Date"
              value={!fromDate ? null : dayjs(fromDate)}
              onChange={(date: Dayjs | null) => setFromDate(date?.format("YYYY-MM-DD") || null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small"
                },
              }}
              sx={{ maxWidth: { md: 180 } }}
            />
            <DatePicker
              label="To Date"
              value={!toDate ? null : dayjs(toDate)}
              onChange={(date: Dayjs | null) => setToDate(date?.format("YYYY-MM-DD") || null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small"
                },
              }}
              sx={{ maxWidth: { md: 180 } }}
            />
            <TextField
              select
              label="Vehicle"
              value={vehicleId || ""}
              onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : null)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {vehicles.map((vehicle) => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.vehicleUnitNumber}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="State"
              value={usState}
              onChange={(e) => setUsState(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              disabled={!vehicleId || !fromDate || !toDate || statesLoading}
              InputProps={{
                endAdornment: statesLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null,
              }}
            >
              <MenuItem value="">All States</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={handleRun} disabled={loading}>
              Run Report
            </Button>
          </Stack>
        </Card>

        {data && (data.lines?.length || 0) > 0 && <IftaTripReportLines lines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container>
  );
}
